// 이벤트 검사 (엔진 M2 3.2 의 표와 E5 3절, 4절의 에디터만의 검사).
//
// 두 무리다.
//   engine  엔진 MapData.validateEvents 와 같은 검사, 같은 경로. 엔진은 이런 이벤트를 건너뛰고 rpg:error 를 찍는다.
//           두 저장소가 같은 픽스처(tests/fixtures/events/invalid_events.json)로 같은 경로 집합을 내는지 대조한다
//   editor  엔진이 모르는 것: 맵 밖, 같은 칸, 없는 참조, 없는 파일, 배열 끝의 null 등. 픽스처에 넣지 않는다
// 경로는 엔진과 같은 1부터 세는 표기다 (events[3].commands[2].branches[1][3].text).
// 2^53을 넘는 정수(맵을 읽을 때 표식 글로 온다, json.ts)는 엔진처럼 수로 본다: 수와 정수 자리를 채우고 글 자리는 채우지 못한다.

import {
  asList,
  bigIntText,
  engineLength,
  field,
  isArrayPlace,
  isInteger,
  isJsonInteger,
  isJsonNumber,
  isJsonText,
  isNonNegInt,
  isNonNegIntValue,
  isObjectPlace,
  isPlainObject,
  jsonNumber,
  jsonValueText,
  type JsonObject,
} from "./json";
import { AREA_KEYS, REF_KEYS, WANDER_KEYS } from "./events";
import { commandSpec, fieldValues, judgedCondition, sheetCount, type ArgSpec, type AssetKind, type CommandSpec, type EventSchema } from "./schema";
import { mapByName, type GameConfig } from "./game";
import { findDuplicateBlocks, placeSuffix } from "./tree";

export type Severity = "error" | "warning" | "info";

export interface EventProblem {
  severity: Severity;
  message: string;
  /** events[3].commands[2].text 꼴 */
  location: string;
  /** engine 이면 엔진도 같은 경로에 같은 문제를 낸다 */
  source: "engine" | "editor";
  eventIndex?: number;
  eventId?: string;
}

export interface PathProblem {
  path: string;
  message: string;
}

/** 맵의 크기와 통행 (MapModel, MapData 가 그대로 맞는다) */
export interface MapGeometry {
  width: number;
  height: number;
  /** 0 지나감, 그 밖은 막힘. 없으면 null */
  collision: readonly number[] | null;
}

export interface ValidateContext {
  schema: EventSchema;
  /** 있으면 맵 밖과 통행을 본다 */
  map?: MapGeometry | null;
  /** 있으면 map 참조를 본다 */
  game?: GameConfig | null;
  /** 있으면 item 참조를 본다 */
  items?: ReadonlySet<string> | null;
  /** 정의 파일(Lua)에서 찾은 이벤트 id (어림). 같은 id 는 게임에서 Lua 가 이긴다 */
  defIds?: ReadonlySet<string> | null;
  /** 있으면 파일 인자와 외형, 얼굴의 파일이 있는지 본다 (프로젝트 기준 경로, ./ 없이) */
  fileExists?: ((projectPath: string) => boolean) | null;
}

type Add = (path: string, message: string) => void;

// ---- 엔진과 같은 검사 ----

const TYPE_NAMES: Record<string, string> = {
  string: "문자열",
  text: "문자열",
  enum: "문자열",
  ref: "id 문자열",
  file: "경로 문자열",
  integer: "정수",
  number: "숫자",
  boolean: "불리언",
  scalar: "불리언, 숫자, 문자열 중 하나",
  json: "JSON 값",
  face: "얼굴 객체",
  options: "항목 배열",
  route: "루트 단계 배열",
  condition: "조건 객체",
};

function typeOfValue(v: unknown): string {
  if (v === null || v === undefined) return "nil";
  if (bigIntText(v) !== null) return "number";
  if (Array.isArray(v) || isPlainObject(v)) return "table";
  return typeof v;
}

const VALUE_TESTS: Record<string, (v: unknown) => boolean> = {
  string: isJsonText,
  text: isJsonText,
  enum: isJsonText,
  ref: isJsonText,
  file: isJsonText,
  integer: isJsonInteger,
  number: isJsonNumber,
  boolean: (v) => typeof v === "boolean",
  scalar: (v) => typeof v === "boolean" || isJsonNumber(v) || isJsonText(v),
  json: () => true,
};

/** 외형과 얼굴 참조의 모양 (엔진 Assets.checkRef). path 는 "" | ".set" | ".file" | ".index" */
export function checkAssetRef(schema: EventSchema, kind: AssetKind, ref: unknown, add: Add): void {
  if (!isObjectPlace(ref)) {
    add("", "객체여야 함");
    return;
  }
  const set = field(ref, "set");
  const file = field(ref, "file");
  const hasSet = set !== undefined;
  const hasFile = file !== undefined;
  if (hasSet && hasFile) add("", "set 과 file 중 하나만 허용");
  else if (!hasSet && !hasFile) add("", "set 또는 file 필요");
  if (hasSet && (!isJsonText(set) || !schema.assets[kind].has(set))) add(".set", `스키마에 없는 ${kind} 이름: ${jsonValueText(set)}`);
  if (hasFile && (!isJsonText(file) || file === "")) add(".file", "경로는 비어 있지 않은 문자열이어야 함");
  const index = field(ref, "index");
  const count = sheetCount(schema, kind);
  const n = jsonNumber(index);
  if (index !== undefined && !(isJsonInteger(index) && n !== undefined && n >= 0 && n < count)) add(".index", `0..${count - 1} 범위의 정수여야 함 (현재: ${jsonValueText(index)})`);
}

function checkStrings(value: unknown, here: string, add: Add, what: string): number | null {
  const list = asList(value);
  if (!list) {
    add(here, `${what} 목록은 배열이어야 함`);
    return null;
  }
  const count = engineLength(list);
  for (let k = 0; k < count; k++) {
    if (!isJsonText(list[k])) add(`${here}[${k + 1}]`, `${what}: 문자열이어야 함 (현재 타입: ${typeOfValue(list[k])})`);
  }
  return count;
}

/** 인자 하나 (엔진 checkArg). 인자 하나에는 문제를 하나만 낸다: 타입, 그다음 빈 경로, 예약 이름, values, 범위 */
export function checkArg(schema: EventSchema, spec: ArgSpec, value: unknown, here: string, add: Add): void {
  if (value === undefined || value === null) {
    if (spec.required) add(here, `값 없음 (${TYPE_NAMES[spec.type] ?? spec.type} 필요)`);
    return;
  }
  switch (spec.type) {
    case "face":
      checkAssetRef(schema, "face", value, (p, m) => add(here + p, `얼굴: ${m}`));
      return;
    case "options": {
      const count = checkStrings(value, here, add, "항목");
      if (count !== null && spec.min !== undefined && count < spec.min) add(here, `항목 ${spec.min}개 이상 필요`);
      return;
    }
    case "route":
      checkStrings(value, here, add, "루트 단계");
      return;
    case "condition": {
      if (!isObjectPlace(value)) {
        add(here, `조건은 객체여야 함 (현재 타입: ${typeOfValue(value)})`);
        return;
      }
      const kind = judgedCondition(schema, value);
      if (kind) for (const a of kind.args) checkArg(schema, a, field(value, a.name), `${here}.${a.name}`, add);
      return;
    }
    case "charset":
    case "wander":
    case "list":
      return;
  }
  const test = VALUE_TESTS[spec.type];
  // 큰 정수의 범위는 가까운 수로 견준다 (보이는 글은 숫자 그대로)
  const n = jsonNumber(value);
  if (test && !test(value)) {
    const shown = n !== undefined ? jsonValueText(value) : typeOfValue(value);
    add(here, `타입 불일치: ${TYPE_NAMES[spec.type]} 필요 (현재: ${shown})`);
  } else if (spec.type === "file" && value === "") {
    add(here, "경로 비어 있음");
  } else if (spec.type === "ref" && (spec.ref === "flag" || spec.ref === "var") && schema.stateReserved.includes(value as string)) {
    add(here, `${String(value)}: 예약된 상태 키(소지품)라 플래그나 변수 이름으로 사용 불가`);
  } else if (spec.values && !spec.values.includes(value as string)) {
    add(here, `${spec.values.join(", ")} 중 하나여야 함 (현재: ${jsonValueText(value)})`);
  } else if (n !== undefined && spec.min !== undefined && n < spec.min) {
    add(here, `${spec.min} 이상이어야 함 (현재: ${jsonValueText(value)})`);
  } else if (n !== undefined && spec.max !== undefined && n > spec.max) {
    add(here, `${spec.max} 이하여야 함 (현재: ${jsonValueText(value)})`);
  }
}

/** 커맨드 하나의 인자와 하위 목록 (엔진 checkList 의 한 칸). here 는 그 커맨드의 경로 */
export function checkCommand(schema: EventSchema, cmd: unknown, here: string, add: Add): void {
  if (!isObjectPlace(cmd)) {
    add(here, "커맨드는 객체여야 함");
    return;
  }
  const code = field(cmd, "code");
  const spec = commandSpec(schema, code);
  if (!spec) {
    add(here, `스키마에 없는 커맨드: ${jsonValueText(code)}`);
    return;
  }
  for (const a of spec.args) checkArg(schema, a, field(cmd, a.name), `${here}.${a.name}`, add);
  // 엔진의 따로 규칙(등록된 스크립트인가) 가운데 에디터도 가릴 수 있는 것: 빈 이름은 등록된 이름일 수 없다.
  // 없는 이름은 필수 검사가 같은 자리에 낸다. 비지 않은 이름은 엔진만 가린다 (에디터만의 정보)
  if (spec.code === "script" && field(cmd, "name") === "") add(`${here}.name`, "등록되지 않은 스크립트 (이름 비어 있음)");
  checkSubLists(schema, spec, cmd, here, add);
}

function checkSubLists(schema: EventSchema, spec: CommandSpec, cmd: unknown, here: string, add: Add): void {
  for (const l of spec.lists) {
    const value = field(cmd, l.name);
    if (value === undefined) continue;
    const at = `${here}.${l.name}`;
    const lists = asList(value);
    if (!lists) {
      add(at, "분기 목록은 배열이어야 함");
      continue;
    }
    if (!l.perOption) {
      checkCommandList(schema, lists, at, add);
      continue;
    }
    for (let b = 0; b < engineLength(lists); b++) {
      const branch = lists[b];
      if (!isArrayPlace(branch)) add(`${at}[${b + 1}]`, "분기 목록은 배열이어야 함");
      else checkCommandList(schema, branch, `${at}[${b + 1}]`, add);
    }
  }
}

/** 커맨드 목록 (엔진 Commands.problems). prefix 는 목록의 경로 */
export function checkCommandList(schema: EventSchema, list: unknown, prefix: string, add: Add): void {
  const items = asList(list);
  if (!items) {
    add(prefix, "커맨드 목록은 배열이어야 함");
    return;
  }
  for (let j = 0; j < engineLength(items); j++) checkCommand(schema, items[j], `${prefix}[${j + 1}]`, add);
}

// 배회 기본값 (엔진 character.lua 의 setWander 와 같다)
const WANDER_MIN = 30;
const WANDER_MAX = 120;

function checkWander(wander: unknown, here: string, add: Add): void {
  if (!isObjectPlace(wander)) {
    add(here, "배회는 객체여야 함");
    return;
  }
  const minWait = field(wander, "minWait");
  const maxWait = field(wander, "maxWait");
  if (minWait !== undefined && !isNonNegIntValue(minWait)) add(`${here}.minWait`, "0 이상의 정수여야 함");
  if (maxWait !== undefined && !isNonNegIntValue(maxWait)) add(`${here}.maxWait`, "0 이상의 정수여야 함");
  const lo = minWait === undefined ? WANDER_MIN : minWait;
  const hi = maxWait === undefined ? WANDER_MAX : maxWait;
  if (isNonNegIntValue(lo) && isNonNegIntValue(hi) && jsonNumber(lo)! > jsonNumber(hi)!) {
    add(`${here}${maxWait !== undefined ? ".maxWait" : ".minWait"}`, `minWait 는 maxWait 이하여야 함 (현재: minWait ${jsonValueText(lo)}, maxWait ${jsonValueText(hi)})`);
  }
  const area = field(wander, "area");
  if (area === undefined) return;
  if (!isObjectPlace(area)) {
    add(`${here}.area`, "배회 영역은 객체여야 함");
    return;
  }
  for (const k of ["x", "y"]) if (!isNonNegIntValue(field(area, k))) add(`${here}.area.${k}`, "0 이상의 정수여야 함");
  for (const k of ["w", "h"]) {
    const v = field(area, k);
    if (!(isJsonInteger(v) && jsonNumber(v)! >= 1)) add(`${here}.area.${k}`, "1 이상의 정수여야 함");
  }
}

/** 이벤트 칸 하나 (commands 는 빼고). here 는 그 칸의 경로. 편집 명령이 새 값을 검사할 때도 쓴다 */
export function checkEventField(schema: EventSchema, name: string, value: unknown, here: string, add: Add): void {
  switch (name) {
    case "id":
      if (!isJsonText(value) || value === "") add(here, "id 는 비어 있지 않은 문자열이어야 함");
      else if (schema.reserved.includes(value)) add(here, `예약된 id: ${value}`);
      return;
    case "x":
    case "y":
      if (!isNonNegIntValue(value)) add(here, `0 이상의 정수여야 함 (현재: ${jsonValueText(value)})`);
      return;
    case "dir":
    case "trigger":
      if (value !== undefined && (!isJsonText(value) || !fieldValues(schema, name).includes(value))) add(here, `지원하지 않는 ${name === "dir" ? "방향" : "트리거"}: ${jsonValueText(value)}`);
      return;
    case "charset":
      if (value !== undefined) checkAssetRef(schema, "charset", value, (p, m) => add(here + p, `외형: ${m}`));
      return;
    case "through":
    case "solid":
      if (value !== undefined && typeof value !== "boolean") add(here, "불리언이어야 함");
      return;
    case "speed":
      if (value !== undefined && !(isJsonNumber(value) && jsonNumber(value)! > 0)) add(here, "0보다 큰 숫자여야 함");
      return;
    case "wander":
      if (value !== undefined) checkWander(value, here, add);
      return;
    case "commands":
      if (value !== undefined) checkCommandList(schema, value, here, add);
      return;
  }
}

const EVENT_FIELDS_CHECKED = ["id", "x", "y", "dir", "trigger", "charset", "through", "solid", "speed", "wander", "commands"];

/** 이벤트 하나 (엔진 checkEvent). seen 은 앞 이벤트들의 id → 1부터 센 번호 */
function checkEvent(schema: EventSchema, ev: unknown, here: string, seen: ReadonlyMap<string, number>, add: Add): void {
  if (!isObjectPlace(ev)) {
    add(here, "이벤트는 객체여야 함");
    return;
  }
  for (const name of EVENT_FIELDS_CHECKED) {
    const value = field(ev, name);
    checkEventField(schema, name, value, `${here}.${name}`, add);
    if (name === "id" && isJsonText(value) && value !== "" && !schema.reserved.includes(value) && seen.has(value)) {
      add(`${here}.id`, `id ${value} 중복 (events[${seen.get(value)}])`);
    }
  }
}

/** 엔진 MapData.validateEvents 와 같은 문제 목록 (경로와 이유) */
export function engineProblems(events: unknown, schema: EventSchema): PathProblem[] {
  const problems: PathProblem[] = [];
  const add: Add = (path, message) => problems.push({ path, message });
  if (events === undefined || events === null) return problems;
  const list = asList(events);
  if (!list) {
    add("events", "이벤트 목록은 배열이어야 함");
    return problems;
  }
  const seen = new Map<string, number>();
  for (let i = 0; i < engineLength(list); i++) {
    const ev = list[i];
    checkEvent(schema, ev, `events[${i + 1}]`, seen, add);
    const id = field(ev, "id");
    if (isObjectPlace(ev) && isJsonText(id) && !seen.has(id)) seen.set(id, i + 1);
  }
  return problems;
}

// ---- 에디터만의 검사 ----

function eventIndexOf(path: string): number | undefined {
  const m = /^events\[(\d+)\]/.exec(path);
  return m ? Number(m[1]) - 1 : undefined;
}

function trailingNull(list: readonly unknown[]): number | null {
  const n = engineLength(list);
  return n < list.length ? n : null;
}

const TRIGGER_DEFAULT = "action";

function cellText(x: number, y: number): string {
  return `${x},${y}`;
}

function extOf(path: string): string {
  const base = path.slice(path.lastIndexOf("/") + 1);
  const dot = base.lastIndexOf(".");
  return dot < 0 ? "" : base.slice(dot + 1).toLowerCase();
}

function bare(path: string): string {
  return path.replace(/\\/g, "/").replace(/^(\.\/)+/, "");
}

interface EditorScope {
  ctx: ValidateContext;
  ids: ReadonlySet<string>;
  /** id → 외형이 있는가 */
  hasCharset: ReadonlyMap<string, boolean>;
  add: (severity: Severity, path: string, message: string) => void;
}

function checkUnknownKeys(o: unknown, known: readonly string[], here: string, what: string, scope: EditorScope): void {
  if (!isPlainObject(o)) return;
  for (const k of Object.keys(o)) {
    if (!known.includes(k) && o[k] !== undefined) scope.add("info", `${here}.${k}`, `스키마에 없는 ${what}: ${k} (저장 시 유지)`);
  }
}

function checkFile(value: unknown, here: string, accept: readonly string[] | undefined, scope: EditorScope): void {
  if (!isJsonText(value) || value === "") return;
  if (accept && accept.length > 0 && !accept.includes(extOf(value))) scope.add("warning", here, `확장자는 ${accept.join(", ")} 중 하나여야 함`);
  const exists = scope.ctx.fileExists;
  if (exists && !exists(bare(value))) scope.add("warning", here, `프로젝트에 없는 파일 ${value}`);
}

function checkRef(spec: ArgSpec, value: unknown, here: string, scope: EditorScope): void {
  if (!isJsonText(value)) return;
  const { ctx } = scope;
  if (value === "") {
    scope.add("warning", here, `${spec.label} 비어 있음`);
    return;
  }
  switch (spec.ref) {
    case "map":
      if (ctx.game && !mapByName(ctx.game, value)) scope.add("warning", here, `rpg-game.json 에 등록되지 않은 맵: ${value}`);
      return;
    case "item":
      if (ctx.items && !ctx.items.has(value)) scope.add("warning", here, `아이템 표에 없는 id: ${value}`);
      return;
    case "character":
      if (ctx.schema.reserved.includes(value)) return;
      if (!scope.ids.has(value)) scope.add("warning", here, `이 맵에 없는 이벤트: ${value}`);
      else if (scope.hasCharset.get(value) === false) scope.add("warning", here, `${value}: 외형(charset) 없음, 이동과 방향 전환 불가 (엔진에서 무시)`);
      return;
  }
}

function checkRouteSteps(value: unknown, here: string, scope: EditorScope): void {
  const list = asList(value);
  if (!list) return;
  const { moves, turnPrefix, waitPrefix } = scope.ctx.schema.route;
  list.forEach((step, k) => {
    if (!isJsonText(step)) return;
    if (moves.includes(step)) return;
    if (step.startsWith(turnPrefix) && moves.includes(step.slice(turnPrefix.length))) return;
    if (step.startsWith(waitPrefix) && /^\d+$/.test(step.slice(waitPrefix.length))) return;
    scope.add("warning", `${here}[${k + 1}]`, `지원하지 않는 루트 단계: ${step} (실행 시 건너뜀)`);
  });
}

function checkArrayTail(value: unknown, here: string, scope: EditorScope): void {
  if (!Array.isArray(value)) return;
  const tail = trailingNull(value);
  if (tail !== null) scope.add("error", `${here}[${tail + 1}]`, "배열 끝의 null 은 엔진에서 무시됨 (삭제 필요)");
}

function checkConditionEditor(cond: unknown, here: string, scope: EditorScope): void {
  if (!isObjectPlace(cond)) return;
  const { schema } = scope.ctx;
  const present = schema.conditions.filter((c) => field(cond, c.kind) !== undefined);
  if (present.length === 0) {
    scope.add("warning", here, "빈 조건 (항상 참이라 '아니면' 분기 실행 안 됨)");
    return;
  }
  if (present.length > 1) scope.add("warning", here, `조건 종류가 2개 이상 (${present.map((c) => c.kind).join(", ")}): 엔진은 첫 번째 ${present[0].kind} 만 사용`);
  const kind = present[0];
  for (const a of kind.args) {
    const v = field(cond, a.name);
    if (a.type === "ref") checkRef(a, v, `${here}.${a.name}`, scope);
  }
  const known = new Set(schema.conditions.flatMap((c) => c.args.map((a) => a.name)));
  checkUnknownKeys(cond, [...known], here, "조건 속성", scope);
}

function checkCommandEditor(cmd: JsonObject, here: string, spec: CommandSpec, scope: EditorScope): void {
  for (const a of spec.args) {
    const v = field(cmd, a.name);
    const at = `${here}.${a.name}`;
    if (v === undefined) continue;
    if (a.type === "ref") checkRef(a, v, at, scope);
    else if (a.type === "file") checkFile(v, at, a.accept, scope);
    else if (a.type === "route") {
      checkArrayTail(v, at, scope);
      checkRouteSteps(v, at, scope);
    } else if (a.type === "options") checkArrayTail(v, at, scope);
    else if (a.type === "condition") checkConditionEditor(v, at, scope);
    else if (a.type === "face") {
      checkUnknownKeys(v, REF_KEYS, at, "속성", scope);
      checkFile(field(v, "file"), `${at}.file`, ["png"], scope);
    }
  }
  // 선택지: 취소가 고르는 항목이 항목 수 안인가
  const options = asList(field(cmd, "options"));
  const cancel = field(cmd, "cancel");
  if (spec.code === "choice" && options && isJsonInteger(cancel) && jsonNumber(cancel)! > engineLength(options)) {
    scope.add("warning", `${here}.cancel`, `취소 키 항목 번호 ${jsonValueText(cancel)}: 항목 수 ${engineLength(options)} 초과`);
  }
  if (spec.code === "transfer" && (field(cmd, "x") === undefined) !== (field(cmd, "y") === undefined)) {
    scope.add("warning", here, "x 와 y 중 하나만 있음 (없는 값은 정의 파일의 시작 좌표 사용)");
  }
  const scriptName = field(cmd, "name");
  if (spec.code === "script" && isJsonText(scriptName) && scriptName !== "") {
    scope.add("info", here, "스크립트 이름은 게임 실행 시에만 확인 가능 (정의 파일의 scripts)");
  }
  checkUnknownKeys(
    cmd,
    ["code", ...spec.args.map((a) => a.name), ...spec.lists.map((l) => l.name)],
    here,
    "인자",
    scope,
  );
  for (const l of spec.lists) {
    const value = field(cmd, l.name);
    if (value === undefined) continue;
    checkArrayTail(value, `${here}.${l.name}`, scope);
    const lists = asList(value);
    if (!lists) continue;
    if (!l.perOption) checkListEditor(lists, `${here}.${l.name}`, scope);
    else
      lists.forEach((branch, b) => {
        const branchList = asList(branch);
        if (branchList) {
          checkArrayTail(branch, `${here}.${l.name}[${b + 1}]`, scope);
          checkListEditor(branchList, `${here}.${l.name}[${b + 1}]`, scope);
        }
      });
  }
}

function checkListEditor(list: readonly unknown[], prefix: string, scope: EditorScope): void {
  const { schema } = scope.ctx;
  let endedBy: CommandSpec | null = null;
  for (let j = 0; j < engineLength(list); j++) {
    const cmd = list[j];
    const here = `${prefix}[${j + 1}]`;
    if (endedBy) {
      scope.add("warning", here, `${endedBy.label} 뒤의 커맨드: 실행되지 않음`);
      endedBy = null;
    }
    if (!isPlainObject(cmd)) continue;
    const spec = commandSpec(schema, cmd.code);
    if (!spec) continue;
    checkCommandEditor(cmd, here, spec, scope);
    if (spec.ends) endedBy = spec;
  }
}

function checkEventEditor(ev: JsonObject, i: number, scope: EditorScope, cells: Map<string, number>, autoCount: { n: number }): void {
  const { ctx } = scope;
  const { schema, map } = ctx;
  const here = `events[${i + 1}]`;
  const x = field(ev, "x");
  const y = field(ev, "y");
  const trigger = field(ev, "trigger") ?? TRIGGER_DEFAULT;
  const hasCell = isNonNegInt(x) && isNonNegInt(y);
  const inMap = hasCell && (!map || (x < map.width && y < map.height));
  if (map && hasCell) {
    if (x >= map.width) scope.add("error", `${here}.x`, `맵 범위 밖 (너비 ${map.width} 타일)`);
    if (y >= map.height) scope.add("error", `${here}.y`, `맵 범위 밖 (높이 ${map.height} 타일)`);
  }
  // 2^53을 넘는 칸은 엔진에게 0 이상의 정수라 엔진 검사를 지나지만 어느 맵에도 들지 않는다
  if (map && bigIntText(x) !== null && isNonNegIntValue(x)) scope.add("error", `${here}.x`, `맵 범위 밖 (너비 ${map.width} 타일)`);
  if (map && bigIntText(y) !== null && isNonNegIntValue(y)) scope.add("error", `${here}.y`, `맵 범위 밖 (높이 ${map.height} 타일)`);
  if (hasCell && (trigger === "action" || trigger === "touch")) {
    const key = `${String(trigger)}:${cellText(x, y)}`;
    const first = cells.get(key);
    if (first !== undefined) scope.add("error", here, `events[${first}] 와 같은 타일(${cellText(x, y)})의 ${String(trigger)} 이벤트 (엔진은 목록에서 앞선 이벤트만 실행)`);
    else cells.set(key, i + 1);
  }
  if (map && inMap && trigger === "touch" && map.collision && (map.collision[y * map.width + x] ?? 0) !== 0) {
    scope.add("warning", here, "touch 이벤트가 통행 불가 타일에 있음 (플레이어 접촉 불가)");
  }
  const charset = field(ev, "charset");
  if (charset !== undefined) {
    checkUnknownKeys(charset, REF_KEYS, `${here}.charset`, "속성", scope);
    checkFile(field(charset, "file"), `${here}.charset.file`, ["png"], scope);
  }
  const wander = field(ev, "wander");
  if (wander !== undefined && isObjectPlace(wander)) {
    if (charset === undefined) scope.add("warning", `${here}.wander`, "외형(charset) 없음: 배회 안 함");
    checkUnknownKeys(wander, WANDER_KEYS, `${here}.wander`, "속성", scope);
    const area = field(wander, "area");
    if (isObjectPlace(area)) {
      checkUnknownKeys(area, AREA_KEYS, `${here}.wander.area`, "속성", scope);
      const [ax, ay, aw, ah] = ["x", "y", "w", "h"].map((k) => field(area, k));
      if (isNonNegInt(ax) && isNonNegInt(ay) && isInteger(aw) && isInteger(ah) && aw >= 1 && ah >= 1) {
        if (map && (ax + aw > map.width || ay + ah > map.height)) scope.add("warning", `${here}.wander.area`, "배회 영역이 맵 밖으로 나감");
        if (hasCell && (x < ax || x >= ax + aw || y < ay || y >= ay + ah)) scope.add("warning", `${here}.wander.area`, "이벤트가 자신의 배회 영역 밖에 있음 (영역 안 타일로만 이동)");
      }
    }
  }
  const id = field(ev, "id");
  if (isJsonText(id) && ctx.defIds?.has(id)) scope.add("warning", `${here}.id`, `정의 파일에 같은 id ${id} 있음: 게임에서는 Lua 정의가 우선`);
  if (trigger === "auto") {
    autoCount.n++;
    scope.add("info", `${here}.trigger`, `맵 진입 시 병합 순서대로 1개씩 실행. 이 맵의 auto 중 ${autoCount.n}번째`);
  }
  checkUnknownKeys(
    ev,
    schema.fields.map((f) => f.name),
    here,
    "이벤트 속성",
    scope,
  );
  const commands = field(ev, "commands");
  if (commands !== undefined) {
    checkArrayTail(commands, `${here}.commands`, scope);
    const list = asList(commands);
    if (list) {
      checkListEditor(list, `${here}.commands`, scope);
      for (const dup of findDuplicateBlocks(list, schema)) {
        const places = dup.places.map((p) => `${here}${placeSuffix(p)}`);
        scope.add("info", places[0], `같은 연속 커맨드 ${dup.size}개가 이 이벤트에 ${places.length}번 반복됨: ${places.join(", ")}. 한 위치만 수정하면 서로 달라짐`);
      }
    }
  }
}

/** 이벤트 목록 전체. 엔진과 같은 검사 뒤에 에디터만의 검사를 붙인다 */
export function validateEvents(events: unknown, ctx: ValidateContext): EventProblem[] {
  const out: EventProblem[] = [];
  const list = events === undefined || events === null ? [] : asList(events);
  const idAt = (i: number | undefined) => {
    if (i === undefined || !list) return undefined;
    const id = field(list[i], "id");
    return isJsonText(id) ? id : undefined;
  };
  const push = (severity: Severity, source: "engine" | "editor", location: string, message: string) => {
    const eventIndex = eventIndexOf(location);
    const p: EventProblem = { severity, message, location, source };
    if (eventIndex !== undefined) {
      p.eventIndex = eventIndex;
      const id = idAt(eventIndex);
      if (id !== undefined) p.eventId = id;
    }
    out.push(p);
  };
  for (const p of engineProblems(events, ctx.schema)) push("error", "engine", p.path, p.message);
  if (!list) return out;

  const ids = new Set<string>();
  const hasCharset = new Map<string, boolean>();
  for (const ev of list) {
    const id = field(ev, "id");
    if (isJsonText(id) && id !== "") {
      ids.add(id);
      if (!hasCharset.has(id)) hasCharset.set(id, field(ev, "charset") !== undefined);
    }
  }
  const scope: EditorScope = { ctx, ids, hasCharset, add: (severity, path, message) => push(severity, "editor", path, message) };
  checkArrayTail(events, "events", scope);
  const cells = new Map<string, number>();
  const autoCount = { n: 0 };
  for (let i = 0; i < engineLength(list); i++) {
    const ev = list[i];
    if (isPlainObject(ev)) checkEventEditor(ev, i, scope, cells, autoCount);
  }
  return out;
}

/** 오류가 있는가 */
export function hasErrors(problems: readonly EventProblem[]): boolean {
  return problems.some((p) => p.severity === "error");
}

/**
 * 정의 파일(Lua)에서 이벤트 id 를 어림으로 찾는다: id = "..." 꼴만. 확실한 확인은 실행할 때 엔진이 찍는
 * rpg:override:<id> 줄이다
 */
export function defFileIds(luaText: string): Set<string> {
  const out = new Set<string>();
  const re = /\bid\s*=\s*(["'])((?:\\.|(?!\1).)*)\1/g;
  for (const line of luaText.split("\n")) {
    const code = line.replace(/--.*$/, "");
    for (const m of code.matchAll(re)) out.add(m[2]);
  }
  return out;
}
